<script setup lang="ts">
/**
 * Schedule editor over a cron expression: pick how often (every N minutes,
 * hourly, daily, on weekdays, weekly, monthly) and when, or type an own
 * expression. A summary line says it in words, the next runs are listed (in
 * the browser's time zone), and a broken own expression shows why.
 *
 * `v-model` is the five-field cron string. Nobody has to type `0 8 * * 1-5`:
 * the structured controls write it; an expression the controls cannot show
 * opens in „Eigener Ausdruck“.
 */
import { computed, inject, ref, useId, watch } from 'vue';
import { i18nInjectionKey } from '../internal';
import { NxNativeSelect, NxTextInput } from '../Inputs';
import {
  buildCron,
  describeCron,
  nextCronRuns,
  NX_CRON_DEFAULT,
  NX_CRON_EVERY_N,
  NX_CRON_WEEKDAYS,
  pad2,
  parseCron,
  validateCron,
  type NxCronFrequency,
  type NxCronSchedule,
} from './cron';

const props = withDefaults(
  defineProps<{
    label?: string;
    disabled?: boolean;
    /** How many upcoming runs to list; 0 hides the list. */
    previewCount?: number;
    /** Shown after the summary, e.g. „Europe/Berlin“ — the runs are still computed locally. */
    timezone?: string;
    frequencyLabels?: Partial<Record<NxCronFrequency, string>>;
    frequencyLabel?: string;
    everyLabel?: string;
    weekdayLabel?: string;
    dayLabel?: string;
    timeLabel?: string;
    hourLabel?: string;
    minuteLabel?: string;
    expressionLabel?: string;
    nextRunsLabel?: string;
    locale?: string;
  }>(),
  {
    label: undefined,
    disabled: false,
    previewCount: 3,
    timezone: undefined,
    frequencyLabels: () => ({}),
    frequencyLabel: 'Wiederholung',
    everyLabel: 'Intervall',
    weekdayLabel: 'Wochentag',
    dayLabel: 'Tag im Monat',
    timeLabel: 'Uhrzeit',
    hourLabel: 'Stunde',
    minuteLabel: 'Minute',
    expressionLabel: 'Cron-Ausdruck',
    nextRunsLabel: 'Nächste Läufe',
    locale: undefined,
  }
);

const model = defineModel<string>({ default: '' });

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value ?? 'de-DE');
const id = useId();

const FREQ_LABELS: Record<NxCronFrequency, string> = {
  every_n_minutes: 'Alle paar Minuten',
  hourly: 'Stündlich',
  daily: 'Täglich',
  weekdays: 'Werktags (Mo–Fr)',
  weekly: 'Wöchentlich',
  monthly: 'Monatlich',
  custom: 'Eigener Ausdruck',
};
const FREQUENCIES = Object.keys(FREQ_LABELS) as NxCronFrequency[];

/** The user picked „Eigener Ausdruck“ — keep the text field even for known shapes. */
const forceCustom = ref(false);
const draft = ref(model.value);
watch(model, (value) => {
  if (value !== draft.value) draft.value = value;
});

const parsed = computed(() => parseCron(model.value));
const schedule = computed<NxCronSchedule>(() => parsed.value ?? { ...NX_CRON_DEFAULT });
const frequency = computed<NxCronFrequency>(() =>
  forceCustom.value || (model.value.trim() && !parsed.value) ? 'custom' : schedule.value.frequency
);

// An empty model starts as the default schedule.
if (!model.value.trim()) model.value = buildCron(NX_CRON_DEFAULT);

function patch(next: Partial<NxCronSchedule>) {
  model.value = buildCron({
    ...schedule.value,
    ...next,
    frequency: next.frequency ?? schedule.value.frequency,
  });
}
function setFrequency(value: string | number | null) {
  const f = String(value) as NxCronFrequency;
  if (f === 'custom') {
    forceCustom.value = true;
    draft.value = model.value;
    return;
  }
  forceCustom.value = false;
  patch({ frequency: f });
}

const error = computed(() => (frequency.value === 'custom' ? validateCron(draft.value) : null));
function onExpression(text: string) {
  draft.value = text;
  if (!validateCron(text)) model.value = text.trim();
}

const frequencyOptions = computed(() =>
  FREQUENCIES.map((f) => ({ value: f, label: props.frequencyLabels[f] ?? FREQ_LABELS[f] }))
);
const everyOptions = NX_CRON_EVERY_N.map((n) => ({ value: n, label: `alle ${n} Minuten` }));
const hourOptions = Array.from({ length: 24 }, (_, h) => ({ value: h, label: pad2(h) }));
const minuteOptions = Array.from({ length: 12 }, (_, i) => ({ value: i * 5, label: pad2(i * 5) }));
const dayOptions = Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: `${i + 1}.` }));
/** Monday first, as people read a week; values stay cron (0 = Sunday). */
const weekOrder = [1, 2, 3, 4, 5, 6, 0];

const needsTime = computed(() =>
  ['daily', 'weekdays', 'weekly', 'monthly'].includes(frequency.value)
);

const summary = computed(() => {
  const text = describeCron(model.value);
  return text && props.timezone ? `${text} (${props.timezone})` : text;
});
const runs = computed(() =>
  props.previewCount > 0 && !error.value
    ? nextCronRuns(model.value, new Date(), props.previewCount)
    : []
);
const runFormat = computed(
  () =>
    new Intl.DateTimeFormat(locale.value, {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
);
</script>

<template>
  <fieldset class="nx-cron-input" :disabled="props.disabled">
    <legend v-if="props.label" class="nx-cron-input__legend">{{ props.label }}</legend>

    <div class="nx-cron-input__row">
      <label class="nx-cron-input__label" :for="`${id}-freq`">{{ props.frequencyLabel }}</label>
      <NxNativeSelect
        :id="`${id}-freq`"
        :model-value="frequency"
        :options="frequencyOptions"
        @update:model-value="setFrequency"
      />
    </div>

    <div v-if="frequency === 'every_n_minutes'" class="nx-cron-input__row">
      <label class="nx-cron-input__label" :for="`${id}-every`">{{ props.everyLabel }}</label>
      <NxNativeSelect
        :id="`${id}-every`"
        :model-value="schedule.everyN"
        :options="everyOptions"
        @update:model-value="patch({ everyN: Number($event) })"
      />
    </div>

    <div v-if="frequency === 'weekly'" class="nx-cron-input__row">
      <span :id="`${id}-wd`" class="nx-cron-input__label">{{ props.weekdayLabel }}</span>
      <div class="nx-cron-input__days" role="radiogroup" :aria-labelledby="`${id}-wd`">
        <button
          v-for="d in weekOrder"
          :key="d"
          type="button"
          role="radio"
          class="nx-cron-input__day"
          :class="{ 'is-on': schedule.weekday === d }"
          :aria-checked="schedule.weekday === d"
          :aria-label="NX_CRON_WEEKDAYS[d]"
          @click="patch({ weekday: d })"
        >
          {{ NX_CRON_WEEKDAYS[d]!.slice(0, 2) }}
        </button>
      </div>
    </div>

    <div v-if="frequency === 'monthly'" class="nx-cron-input__row">
      <label class="nx-cron-input__label" :for="`${id}-dom`">{{ props.dayLabel }}</label>
      <NxNativeSelect
        :id="`${id}-dom`"
        :model-value="schedule.dayOfMonth"
        :options="dayOptions"
        @update:model-value="patch({ dayOfMonth: Number($event) })"
      />
    </div>

    <div v-if="needsTime || frequency === 'hourly'" class="nx-cron-input__row">
      <span :id="`${id}-time`" class="nx-cron-input__label">
        {{ frequency === 'hourly' ? props.minuteLabel : props.timeLabel }}
      </span>
      <div class="nx-cron-input__time" role="group" :aria-labelledby="`${id}-time`">
        <NxNativeSelect
          v-if="needsTime"
          :model-value="schedule.hour"
          :options="hourOptions"
          :aria-label="props.hourLabel"
          @update:model-value="patch({ hour: Number($event) })"
        />
        <span v-if="needsTime" aria-hidden="true">:</span>
        <NxNativeSelect
          :model-value="
            minuteOptions.some((o) => o.value === schedule.minute) ? schedule.minute : null
          "
          :options="minuteOptions"
          :placeholder="pad2(schedule.minute)"
          :aria-label="props.minuteLabel"
          @update:model-value="patch({ minute: Number($event) })"
        />
      </div>
    </div>

    <div v-if="frequency === 'custom'" class="nx-cron-input__row">
      <label class="nx-cron-input__label" :for="`${id}-expr`">{{ props.expressionLabel }}</label>
      <div class="nx-cron-input__expr">
        <NxTextInput
          :id="`${id}-expr`"
          class="nx-cron-input__mono"
          :model-value="draft"
          placeholder="0 8 * * 1-5"
          :aria-invalid="!!error || undefined"
          :aria-describedby="error ? `${id}-err` : undefined"
          @update:model-value="onExpression"
        />
        <p v-if="error" :id="`${id}-err`" class="nx-cron-input__error" role="alert">{{ error }}</p>
      </div>
    </div>

    <p v-if="summary && !error" class="nx-cron-input__summary" data-testid="nx-cron-summary">
      {{ summary }}
    </p>
    <div v-if="runs.length" class="nx-cron-input__runs">
      <span class="nx-cron-input__label">{{ props.nextRunsLabel }}</span>
      <ol>
        <li v-for="run in runs" :key="run.getTime()">
          <time :datetime="run.toISOString()">{{ runFormat.format(run) }}</time>
        </li>
      </ol>
    </div>
  </fieldset>
</template>

<style scoped>
.nx-cron-input {
  display: grid;
  grid-template-columns: minmax(calc(var(--nxt-size-unit) * 7), max-content) minmax(0, 1fr);
  align-items: center;
  gap: var(--nxt-space-sm) var(--nxt-space-md);
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}
.nx-cron-input__legend {
  grid-column: 1 / -1;
  margin-block-end: var(--nxt-space-xs);
  padding: 0;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-cron-input__row {
  display: contents;
}
.nx-cron-input__label {
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
}
.nx-cron-input__days {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-2xs);
}
.nx-cron-input__day {
  min-width: var(--nxt-control-size-sm);
  height: var(--nxt-control-size-sm);
  padding: 0 var(--nxt-space-xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg-subtle);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  cursor: pointer;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-cron-input__day.is-on {
  border-color: var(--nxt-brand-bg);
  background: var(--nxt-brand-bg);
  color: var(--nxt-fg-on-accent);
}
.nx-cron-input__day:focus-visible {
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-cron-input__time {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
}
.nx-cron-input__expr {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
.nx-cron-input__mono :deep(input) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.nx-cron-input__error {
  margin: 0;
  color: var(--nxt-danger-subtle-fg);
  font-size: var(--nxt-font-size-xs);
}
.nx-cron-input__summary {
  grid-column: 1 / -1;
  margin: 0;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-cron-input__runs {
  display: contents;
}
.nx-cron-input__runs ol {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
  font-variant-numeric: tabular-nums;
}
@media (prefers-reduced-motion: reduce) {
  .nx-cron-input__day {
    transition: none;
  }
}
</style>
